import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from '@ohpen/enso-ui';

import { ConfigTableActions } from '@config/components';
import { CONFIG_PAGINATION, CONFIG_SCROLL } from '@config/utils';
import { AREA_LIST_NAME, TRANSLATION_SOURCE_LIST_NAME, translationColumns } from '../tableConfig';
import { ENSO_NAMESPACE, HOST_NAMESPACE, translationRows } from '../utils';

export const TranslationsList = (): ReactElement => {
  const { t, i18n: instance, ready } = useTranslation();

  // Read through the hook's instance and only once it reports the namespace loaded. Reading the
  // global one early flattens nothing and, keyed on the language alone, never recomputes.
  const bundle = (lng: string, ns: string): unknown => (ready ? instance.getResourceBundle(lng, ns) : undefined);
  const enHost = bundle('en', HOST_NAMESPACE);
  const nlHost = bundle('nl', HOST_NAMESPACE);
  const enEnso = bundle('en', ENSO_NAMESPACE);
  const nlEnso = bundle('nl', ENSO_NAMESPACE);

  const dataSource = useMemo(
    () => translationRows({ en: { host: enHost, enso: enEnso }, nl: { host: nlHost, enso: nlEnso } }),
    [enHost, nlHost, enEnso, nlEnso],
  );

  const sources = useMemo(
    () => ({
      enso: t('config.translations.sources.enso'),
      override: t('config.translations.sources.override'),
      app: t('config.translations.sources.app'),
    }),
    [t],
  );

  const valueLists = useMemo(() => {
    const areas: Record<string, string> = {};
    dataSource.forEach(row => {
      areas[row.area] = row.area;
    });

    return { [AREA_LIST_NAME]: areas, [TRANSLATION_SOURCE_LIST_NAME]: sources };
  }, [dataSource, sources]);

  return (
    <Table
      id="configTranslations"
      rowKey="key"
      dataSource={dataSource}
      columns={translationColumns}
      valueLists={valueLists}
      loading={!ready}
      pagination={CONFIG_PAGINATION}
      scroll={CONFIG_SCROLL}
      resizableColumns
      itemsLabel={t('config.sections.translations')}
    >
      <ConfigTableActions />
    </Table>
  );
};
